import React, { useState, useMemo, useRef } from "react";
import { Image as ImageIcon, Edit2, Save, X, Trash2, Plus, ChevronLeft, ChevronRight, Upload, Loader2 } from "lucide-react";
import { apiService } from "../../service/Api";

/**
 * Reusable Image Gallery Editor Component
 * Allows users to view, add, edit, and remove images with titles
 * Supports pagination (10 images per page)
 * Supports file upload to S3
 */
export default function ImageGalleryEditor({ images = [], onChange, title = "Images", imagesPerPage = 10, enableUpload = true }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", link: "" });
  const [addingNew, setAddingNew] = useState(false);
  const [addMode, setAddMode] = useState("url"); // "url" or "upload"
  const [newImage, setNewImage] = useState({ title: "", link: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Pagination calculations
  const totalPages = Math.ceil(images.length / imagesPerPage);
  const startIndex = (currentPage - 1) * imagesPerPage;
  const endIndex = startIndex + imagesPerPage;
  const paginatedImages = useMemo(() => {
    return images.slice(startIndex, endIndex);
  }, [images, startIndex, endIndex]);

  const handleEdit = (globalIndex) => {
    setEditingIndex(globalIndex);
    setEditForm({ ...images[globalIndex] });
  };

  const handleSave = () => {
    const updated = [...images];
    updated[editingIndex] = editForm;
    onChange(updated);
    setEditingIndex(null);
    setEditForm({ title: "", link: "" });
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditForm({ title: "", link: "" });
  };

  const handleRemove = (globalIndex) => {
    const updated = images.filter((_, i) => i !== globalIndex);
    onChange(updated);
    // Adjust current page if needed
    const newTotalPages = Math.ceil(updated.length / imagesPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  const handleAddNew = () => {
    if (newImage.link.trim()) {
      onChange([...images, newImage]);
      setNewImage({ title: "", link: "" });
      setAddingNew(false);
      // Go to last page to see the new image
      const newTotalPages = Math.ceil((images.length + 1) / imagesPerPage);
      setCurrentPage(newTotalPages);
    }
  };

  const handleCancelAdd = () => {
    setNewImage({ title: "", link: "" });
    setAddingNew(false);
    setAddMode("url");
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setUploadError(null);

    try {
      // Upload to S3
      const response = await apiService.uploadTailspinImages(files, {
        saveToDb: false, // We'll handle adding to the array ourselves
        titles: files.map(f => f.name.replace(/\.[^/.]+$/, "")) // Remove extension
      });

      if (response.data.success && response.data.images) {
        // Add uploaded images to the gallery
        const uploadedImages = response.data.images.map(img => ({
          title: img.title,
          link: img.link
        }));
        
        onChange([...images, ...uploadedImages]);
        
        // Navigate to last page
        const newTotalPages = Math.ceil((images.length + uploadedImages.length) / imagesPerPage);
        setCurrentPage(newTotalPages);
        
        setAddingNew(false);
        setAddMode("url");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        setUploadError(response.data.message || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(error.response?.data?.message || "Failed to upload images");
    } finally {
      setUploading(false);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {title} ({images.length})
          </h3>
          {totalPages > 1 && (
            <p className="text-sm text-gray-500 mt-1">
              Page {currentPage} of {totalPages}
            </p>
          )}
        </div>
        <button
          onClick={() => setAddingNew(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          Add Image
        </button>
      </div>

      {images.length === 0 && !addingNew ? (
        <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <p className="text-lg mb-2">No images yet</p>
          <p className="text-sm">Click "Add Image" to get started</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedImages.map((image, paginatedIndex) => {
              const globalIndex = startIndex + paginatedIndex;
              return (
                <div
                  key={globalIndex}
                  className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors"
                >
                  {editingIndex === globalIndex ? (
                <div className="p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Title (optional)
                    </label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) =>
                        setEditForm({ ...editForm, title: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Image title"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Image URL
                    </label>
                    <input
                      type="text"
                      value={editForm.link}
                      onChange={(e) =>
                        setEditForm({ ...editForm, link: e.target.value })
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 flex items-center justify-center gap-1"
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center justify-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative aspect-video bg-gray-100">
                    <img
                      src={image.link}
                      alt={image.title || "Image"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src =
                          'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button
                        onClick={() => handleEdit(globalIndex)}
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      <button
                        onClick={() => handleRemove(globalIndex)}
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-900 font-medium line-clamp-2">
                      {image.title || "Untitled"}
                    </p>
                  </div>
                </>
              )}
            </div>
          );
        })}

            {/* Add New Image Card */}
            {addingNew && (
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 space-y-3 bg-blue-50">
                {/* Mode Toggle */}
                {enableUpload && (
                  <div className="flex gap-2 mb-3">
                    <button
                      onClick={() => setAddMode("url")}
                      className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                        addMode === "url"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300"
                      }`}
                    >
                      From URL
                    </button>
                    <button
                      onClick={() => setAddMode("upload")}
                      className={`flex-1 px-3 py-2 text-sm rounded transition-colors ${
                        addMode === "upload"
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 border border-gray-300"
                      }`}
                    >
                      Upload File
                    </button>
                  </div>
                )}

                {addMode === "url" ? (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Title (optional)
                      </label>
                      <input
                        type="text"
                        value={newImage.title}
                        onChange={(e) =>
                          setNewImage({ ...newImage, title: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Image title"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Image URL *
                      </label>
                      <input
                        type="text"
                        value={newImage.link}
                        onChange={(e) =>
                          setNewImage({ ...newImage, link: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddNew}
                        disabled={!newImage.link.trim()}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        Add
                      </button>
                      <button
                        onClick={handleCancelAdd}
                        className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 flex items-center justify-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">
                        Upload Images
                      </label>
                      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg bg-white">
                        <Upload className="w-10 h-10 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 mb-2">
                          Click to select images or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 mb-3">
                          PNG, JPG, GIF, WebP, SVG up to 10MB each
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/svg+xml"
                          multiple
                          onChange={handleFileSelect}
                          disabled={uploading}
                          className="hidden"
                          id="file-upload"
                        />
                        <label
                          htmlFor="file-upload"
                          className={`px-4 py-2 bg-blue-600 text-white text-sm rounded cursor-pointer hover:bg-blue-700 ${
                            uploading ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          {uploading ? (
                            <span className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Uploading...
                            </span>
                          ) : (
                            "Select Files"
                          )}
                        </label>
                      </div>
                      {uploadError && (
                        <p className="text-xs text-red-600 mt-2">{uploadError}</p>
                      )}
                    </div>
                    <button
                      onClick={handleCancelAdd}
                      disabled={uploading}
                      className="w-full px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1}-{Math.min(endIndex, images.length)} of {images.length} images
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                {/* Page Numbers */}
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage = 
                      page === 1 || 
                      page === totalPages || 
                      (page >= currentPage - 1 && page <= currentPage + 1);
                    
                    const showEllipsis = 
                      (page === currentPage - 2 && currentPage > 3) ||
                      (page === currentPage + 2 && currentPage < totalPages - 2);

                    if (showEllipsis) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }

                    if (!showPage) {
                      return null;
                    }

                    return (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`px-3 py-2 rounded-lg ${
                          currentPage === page
                            ? "bg-blue-600 text-white"
                            : "border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

